import { useState, useRef } from 'react';
import { PageLayout } from '@/components/PageLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Upload, Download, FileSpreadsheet, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSectorTerms } from '@/utils/useSectorTerms';

export const AdminBulkImport = () => {
  const { user: currentUser } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;
  const fileInputRef = useRef(null);

  const labels = currentUser?.labels || { people: 'Kullanıcılar', payment: 'Ödeme', identifier: 'Numara' };
  const terms = useSectorTerms();

  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!['csv', 'xlsx', 'xls'].includes(ext)) {
        toast.error('Sadece CSV veya Excel (.xlsx) dosyası yüklenebilir');
        return;
      }
      setSelectedFile(file);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const { data } = await axios.post(`${API_URL}/api/users/bulk-import`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
      if (data.success > 0) {
        toast.success(`${data.success} kullanıcı başarıyla eklendi`);
      }
      if (data.errors?.length > 0) {
        toast.error(`${data.errors.length} satırda hata var`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Yükleme başarısız');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users/import-template`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'kullanici_sablonu.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Şablon indirildi');
    } catch (error) {
      toast.error('Şablon indirilemedi');
    }
  };

  const resetUpload = () => {
    setSelectedFile(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <PageLayout>
      
      
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A]" data-testid="bulk-import-title">
              Toplu {terms.customerLabelPlural} Aktarımı
            </h1>
            <p className="text-base text-[#595959] mt-2">
              CSV veya Excel dosyası ile toplu {labels.people.toLowerCase()} ekleyin
            </p>
          </div>

          <div className="space-y-6">
            {/* Template Download */}
            <Card className="border border-[#E5E5E5]" data-testid="template-card">
              <CardHeader>
                <CardTitle className="text-lg text-[#2563EB] flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5" strokeWidth={1.5} />
                  Adım 1: Şablonu İndirin
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-[#595959] mb-4">
                  Aşağıdaki Excel şablonunu indirip doldurun. Sütunlar: <strong>Ad Soyad</strong>, <strong>E-posta</strong>, <strong>Şifre</strong>, <strong>{terms.customerNoLabel}</strong>, <strong>Telefon</strong>
                </p>
                <Button
                  variant="outline"
                  onClick={handleDownloadTemplate}
                  className="gap-2"
                  data-testid="download-template-button"
                >
                  <Download className="w-4 h-4" />
                  Şablonu İndir (Excel)
                </Button>
              </CardContent>
            </Card>

            {/* File Upload */}
            <Card className="border border-[#E5E5E5]" data-testid="upload-card">
              <CardHeader>
                <CardTitle className="text-lg text-[#2563EB] flex items-center gap-2">
                  <Upload className="w-5 h-5" strokeWidth={1.5} />
                  Adım 2: Dosyayı Yükleyin
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className="border-2 border-dashed border-[#E5E5E5] rounded-xl p-8 text-center cursor-pointer hover:border-[#2563EB] transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="file-drop-zone"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="file-input"
                  />
                  {selectedFile ? (
                    <div className="flex flex-col items-center gap-2">
                      <FileSpreadsheet className="w-10 h-10 text-[#2563EB]" />
                      <p className="font-medium text-[#1A1A1A]">{selectedFile.name}</p>
                      <p className="text-sm text-[#595959]">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="w-10 h-10 text-[#595959]" />
                      <p className="text-[#595959]">Dosya seçmek için tıklayın</p>
                      <p className="text-sm text-[#595959]">CSV veya Excel (.xlsx)</p>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 mt-4">
                  <Button
                    onClick={handleUpload}
                    disabled={!selectedFile || uploading}
                    className="bg-[#2563EB] hover:bg-[#1E4ED8] text-white gap-2"
                    data-testid="upload-button"
                  >
                    <Upload className="w-4 h-4" />
                    {uploading ? 'Yükleniyor...' : 'Yükle ve Aktar'}
                  </Button>
                  {selectedFile && (
                    <Button variant="outline" onClick={resetUpload} data-testid="reset-button">
                      Sıfırla
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Results */}
            {result && (
              <Card className="border border-[#E5E5E5]" data-testid="result-card">
                <CardHeader>
                  <CardTitle className="text-lg text-[#2563EB]">Sonuç</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                    <div className="bg-[#F9F8F6] rounded-lg p-4 text-center" data-testid="result-total">
                      <p className="text-2xl font-bold text-[#1A1A1A]">{result.total}</p>
                      <p className="text-sm text-[#595959]">Toplam Satır</p>
                    </div>
                    <div className="bg-[#E8F5E9] rounded-lg p-4 text-center" data-testid="result-success">
                      <p className="text-2xl font-bold text-[#16A34A] flex items-center justify-center gap-1">
                        <CheckCircle2 className="w-5 h-5" />
                        {result.success}
                      </p>
                      <p className="text-sm text-[#595959]">Başarılı</p>
                    </div>
                    <div className={`rounded-lg p-4 text-center ${result.errors?.length > 0 ? 'bg-[#FDE8E4]' : 'bg-[#F9F8F6]'}`} data-testid="result-errors">
                      <p className={`text-2xl font-bold flex items-center justify-center gap-1 ${result.errors?.length > 0 ? 'text-[#DC2626]' : 'text-[#595959]'}`}>
                        {result.errors?.length > 0 && <XCircle className="w-5 h-5" />}
                        {result.errors?.length || 0}
                      </p>
                      <p className="text-sm text-[#595959]">Hatalı</p>
                    </div>
                  </div>

                  {result.errors?.length > 0 && (
                    <div data-testid="error-table-container">
                      <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle className="w-4 h-4 text-[#DC2626]" />
                        <h3 className="font-medium text-[#DC2626]">Hatalı Satırlar</h3>
                      </div>
                      <div className="bg-white border border-[#E5E5E5] rounded-xl overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Satır</TableHead>
                              <TableHead>E-posta</TableHead>
                              <TableHead>Hata</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {result.errors.map((err, i) => (
                              <TableRow key={i} data-testid={`error-row-${i}`}>
                                <TableCell>{err.row}</TableCell>
                                <TableCell>{err.email}</TableCell>
                                <TableCell className="text-[#DC2626]">{err.error}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      
    </PageLayout>
  );
};
